'use client'

import { useMemo } from 'react'
import { Skeleton } from '@dd3tech/wiggui/Skeleton'
import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid
} from 'recharts'

import { CustomTooltip, CustomXAxis, CustomYAxis } from './components'
import { composeClasses } from '@wiggot/utils'

interface Point {
  year: number
  value: number
}

interface CapitalGainChartProps {
  timeSeries?: Point[]
  scale: number
  gridStroke?: string
  height: string
  className?: string
  isLoading?: boolean
}

export const CapitalGainChart = ({
  timeSeries,
  scale,
  gridStroke = '#BABDD5',
  height,
  className,
  isLoading
}: CapitalGainChartProps) => {
  const history = useMemo(() => {
    return timeSeries?.map((item) => ({
      name: item.year,
      value: item.value * scale
    }))
  }, [scale, timeSeries])

  if (isLoading) {
    return <Skeleton width="w-full" height={height} />
  }

  return (
    <div className={composeClasses('w-full', height, className)}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          width={500}
          height={145}
          data={history}
          margin={{
            top: 10,
            right: 20,
            left: -20,
            bottom: -10
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
          <YAxis
            domain={[(dataMin: number) => dataMin * 0.9, 'auto']}
            tickSize={0}
            minTickGap={5}
            tickCount={5}
            interval={0}
            stroke="transparent"
            tick={<CustomYAxis />}
          />
          <XAxis
            dataKey="name"
            fontSize={12}
            stroke="transparent"
            tickSize={0}
            interval={0}
            tick={<CustomXAxis />}
          />
          <Tooltip content={CustomTooltip} />
          <Line
            type="monotone"
            dataKey="value"
            stroke="#0038FF"
            activeDot={{ r: 8 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}
